import React from "react";
import { Input } from "../../ui/input";
import { TAB_EMAIL, TAB_SMS, TAB_X } from "./BazookaChannelTabs";

// BazookaFormFields — the channel-aware input rows for the Bazooka
// firing form. Extracted from ChairmansBazooka (Iter-58.6 split) so
// the parent shrinks to orchestration only and the field rendering
// can evolve independently.
//
// Renders, in order:
//   1. First / Last name (required for email & SMS, optional for X DM)
//   2. Username pre-pick (always optional)
//   3. Channel-specific field — Email | Phone | X handle
//
// Fully controlled — every state slice + setter is passed in. No state
// of its own.
const BazookaFormFields = ({
  tab,
  firstName, setFirstName,
  lastName, setLastName,
  usernamePick, setUsernamePick,
  email, setEmail,
  phone, setPhone,
  xHandle, setXHandle,
}) => {
  const nameRequiredHint = tab === TAB_X
    ? <span className="text-gray-500 font-normal">(optional in X mode)</span>
    : "*";

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-gray-300 text-xs block mb-1">
            First name {nameRequiredHint}
          </label>
          <Input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Clive"
            data-testid="bazooka-first-name"
            className="bg-gray-900/60 border-gray-700 text-white"
            autoComplete="off"
          />
        </div>
        <div>
          <label className="text-gray-300 text-xs block mb-1">
            Last name {nameRequiredHint}
          </label>
          <Input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Owen"
            data-testid="bazooka-last-name"
            className="bg-gray-900/60 border-gray-700 text-white"
            autoComplete="off"
          />
        </div>
      </div>

      <div>
        {/* Optional username pre-pick — backend validates uniqueness +
            reserved-list at fire time so the admin learns about
            collisions immediately, not after the recipient clicks. */}
        <label className="text-gray-300 text-xs block mb-1">
          Username{" "}
          <span className="text-gray-500 font-normal">
            (optional — leave blank and they'll pick their own)
          </span>
        </label>
        <Input
          value={usernamePick}
          onChange={(e) =>
            setUsernamePick(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))
          }
          placeholder="e.g. clivetv"
          data-testid="bazooka-username"
          className="bg-gray-900/60 border-gray-700 text-white"
          autoComplete="off"
          maxLength={30}
        />
      </div>

      {tab === TAB_EMAIL && (
        <div>
          <label className="text-gray-300 text-xs block mb-1">Email *</label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="creator@example.com"
            data-testid="bazooka-email"
            className="bg-gray-900/60 border-gray-700 text-white"
            autoComplete="off"
          />
        </div>
      )}

      {tab === TAB_SMS && (
        <div>
          <label className="text-gray-300 text-xs block mb-1">
            Mobile number * (E.164, e.g. +15551234567)
          </label>
          <Input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+15551234567"
            data-testid="bazooka-phone"
            className="bg-gray-900/60 border-gray-700 text-white"
            autoComplete="off"
          />
        </div>
      )}

      {tab === TAB_X && (
        <div>
          <label className="text-gray-300 text-xs block mb-1">X handle *</label>
          <div className="flex items-center gap-1 bg-gray-900/60 border border-gray-700 rounded-md px-3">
            <span className="text-gray-500 text-sm">@</span>
            <Input
              type="text"
              value={xHandle}
              onChange={(e) =>
                setXHandle(e.target.value.replace(/[^A-Za-z0-9_]/g, "").slice(0, 15))
              }
              placeholder="creator_handle"
              data-testid="bazooka-x-handle"
              className="bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-white px-1"
              autoComplete="off"
              maxLength={15}
            />
          </div>
          <p className="text-[10px] text-gray-500 mt-1 leading-tight">
            We'll mint the invite + a copy-paste DM template. You'll send the
            actual DM from x.com (X doesn't allow us to send DMs on your behalf yet).
          </p>
        </div>
      )}
    </>
  );
};

export default BazookaFormFields;
